import { useEffect } from "react";
import { hasSave } from "./game/save";

// Global keyboard support: Enter/Escape/Space, digits + Backspace on the
// keypad, and arrow-key navigation (with inverted highlight) over the
// on-canvas option rects. All inputs route through the IO bridge.
export function useGameKeyboard({ ioRef, soundRef, phaseRef, controlsRef, showSettingsRef, startGame }) {
  useEffect(() => {
    const onKey = (e) => {
      const io = ioRef.current;
      if (!io) return;
      const tag = e.target && e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (showSettingsRef.current) return;
      const ph = phaseRef.current;
      if (ph === "title") {
        if (e.key === "Enter") {
          e.preventDefault();
          startGame(hasSave());
        }
        return;
      }
      if (ph !== "playing") return;
      if (soundRef.current) soundRef.current.ensure();
      const ctl = controlsRef.current || {};
      if (io.helpOpen) {
        if (e.key === "Enter" || e.key === "Escape" || e.key === " ") {
          e.preventDefault();
          io.toggleHelp();
        }
        return;
      }
      switch (io.mode) {
        case "key":
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            io.handleCanvasTap(0, 0);
          }
          break;
        case "options":
          if (e.key === "ArrowDown" || e.key === "ArrowLeft") {
            e.preventDefault();
            io.keyNavOption(1);
          } else if (e.key === "ArrowUp" || e.key === "ArrowRight") {
            e.preventDefault();
            io.keyNavOption(-1);
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (!io.keyConfirmOption()) io.keyNavOption(1);
          } else if (e.key === "Escape" && ctl.escapable !== false) {
            e.preventDefault();
            io.pressEscape();
          }
          break;
        case "number":
          if (/^[0-9]$/.test(e.key)) {
            e.preventDefault();
            io.pressDigit(parseInt(e.key, 10));
          } else if (e.key === "Backspace") {
            e.preventDefault();
            io.pressBackspace();
          } else if (e.key === "Enter") {
            e.preventDefault();
            io.pressNumberConfirm();
          } else if (e.key === "Escape" && ctl.escapable !== false) {
            e.preventDefault();
            io.pressEscape();
          }
          break;
        case "yesno":
          if (e.key === "Enter" || e.key.toLowerCase() === "y" || e.key === "כ") {
            e.preventDefault();
            io.pressYes();
          } else if (e.key === "Escape" || e.key.toLowerCase() === "n" || e.key === "ל") {
            e.preventDefault();
            io.pressNo();
          }
          break;
        case "choice": {
          const keys = ctl.keys || [];
          if (e.key === "Enter" && keys.some((k) => k.key === "enter")) {
            e.preventDefault();
            io.pressChoice("enter");
          } else if (e.key === "Escape" && keys.some((k) => k.key === "esc")) {
            e.preventDefault();
            io.pressChoice("esc");
          }
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ioRef, soundRef, phaseRef, controlsRef, showSettingsRef, startGame]);
}
